import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n/I18nContext";
import ProtectedRoute, { Guard } from "@/components/control/ProtectedRoute";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/public/ForgotPassword";
import ResetPassword from "@/pages/public/ResetPassword";
import AcceptInvitation from "@/pages/public/AcceptInvitation";
import TwoFaRecoveryLink from "@/pages/public/TwoFaRecoveryLink";
import Overview from "@/pages/Overview";
import Search from "@/pages/Search";
import Account from "@/pages/Account";
import Members from "@/pages/Members";
import MemberDetail from "@/pages/MemberDetail";
import Reviews from "@/pages/Reviews";
import ReviewDetail from "@/pages/ReviewDetail";
import Activities from "@/pages/Activities";
import ActivityDetail from "@/pages/ActivityDetail";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import CommunityPosts from "@/pages/CommunityPosts";
import CommunityPostDetail from "@/pages/CommunityPostDetail";
import Organizations from "@/pages/Organizations";
import OrganizationDetail from "@/pages/OrganizationDetail";
import Support from "@/pages/Support";
import SupportDetail from "@/pages/SupportDetail";
import Content from "@/pages/Content";
import ContentDetail from "@/pages/ContentDetail";
import Notifications from "@/pages/Notifications";
import NotificationDetail from "@/pages/NotificationDetail";
import Pilot from "@/pages/Pilot";
import AuditLogs from "@/pages/AuditLogs";
import SystemStatus from "@/pages/SystemStatus";
import Settings from "@/pages/Settings";
import BothApps from "@/pages/BothApps";
import MyTasks from "@/pages/MyTasks";
import TeamRights from "@/pages/TeamRights";
import OrgClaims, { OrgClaimDetail } from "@/pages/OrgClaims";
import LocationReview from "@/pages/LocationReview";
import OrgRights from "@/pages/OrgRights";
import AccessGaps, { AccessGapDetail } from "@/pages/AccessGaps";
import Appeals from "@/pages/Appeals";
import TextReviews from "@/pages/TextReviews";
import SharedComponents from "@/pages/SharedComponents";
import QualityChecks from "@/pages/QualityChecks";
import OpsEffort from "@/pages/OpsEffort";
import Help from "@/pages/Help";
import NotFound from "@/pages/NotFound";
import SaveView from "@/pages/SaveView";
import InviteMember from "@/pages/InviteMember";

// V3 views C101–C120 (static segments win over the ":id" detail routes).
const V3_ROUTES = [
  ["/aufgaben", "tasks", MyTasks], ["/team-rechte", "team", TeamRights],
  ["/organisationen/ansprueche", "organizations", OrgClaims], ["/organisationen/ansprueche/:id", "organizations", OrgClaimDetail],
  ["/organisationen/:id/standorte/:locationId", "organizations", LocationReview],
  ["/organisationen/:id/rechte", "organizations", OrgRights], ["/organisationsrechte", "organizations", OrgRights],
  ["/aktivitaeten/zugangsluecken", "activities", AccessGaps], ["/aktivitaeten/zugangsluecken/:id", "activities", AccessGapDetail],
  ["/meldungen-sicherheit/einsprueche", "reports", Appeals], ["/inhalte-freigaben/textpruefung", "content", TextReviews],
  ["/komponenten", "system", SharedComponents], ["/qualitaet", "system", QualityChecks], ["/qualitaet/:kind", "system", QualityChecks],
  ["/betriebsaufwand", "system", OpsEffort], ["/hilfe", "help", Help],
  ["/aufgaben/ansicht-speichern", "tasks", SaveView], ["/mitglieder/einladen", "members", InviteMember, "members:manage"],
];

const AREAS = [
  ["/mitglieder", "members", Members, MemberDetail],
  ["/profilpruefungen", "reviews", Reviews, ReviewDetail],
  ["/aktivitaeten", "activities", Activities, ActivityDetail],
  ["/kreise-gruppen", "groups", Groups, GroupDetail],
  ["/meldungen-sicherheit", "reports", Reports, ReportDetail],
  ["/community-beitraege", "community", CommunityPosts, CommunityPostDetail],
  ["/organisationen", "organizations", Organizations, OrganizationDetail],
  ["/support", "support", Support, SupportDetail],
  ["/inhalte-freigaben", "content", Content, ContentDetail],
  ["/benachrichtigungen", "notifications", Notifications, NotificationDetail],
];

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/passwort-vergessen" element={<ForgotPassword />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
            <Route path="/einladung" element={<AcceptInvitation />} />
            <Route path="/2fa-wiederherstellung" element={<TwoFaRecoveryLink />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Guard menuKey="overview"><Overview /></Guard>} />
              <Route path="/suche" element={<Guard menuKey="overview"><Search /></Guard>} />
              <Route path="/konto" element={<Account />} />
              <Route path="/beide-apps" element={<Guard menuKey="both_apps"><BothApps /></Guard>} />
              {AREAS.map(([path, key, List, Detail]) => [
                <Route key={path} path={path} element={<Guard menuKey={key} list><List /></Guard>} />,
                <Route key={`${path}-d`} path={`${path}/:id`} element={<Guard menuKey={key}><Detail /></Guard>} />,
                <Route key={`${path}-a`} path={`${path}/aktion/:aktion`} element={<Guard menuKey={key} list><List /></Guard>} />,
                <Route key={`${path}-da`} path={`${path}/:id/:aktion`} element={<Guard menuKey={key}><Detail /></Guard>} />,
              ])}
              {V3_ROUTES.map(([path, key, Page, perm]) => (
                <Route key={path} path={path} element={<Guard menuKey={key} perm={perm}><Page /></Guard>} />
              ))}
              <Route path="/pilot-auswertung" element={<Guard menuKey="pilot"><Pilot /></Guard>} />
              <Route path="/audit-log" element={<Guard menuKey="audit"><AuditLogs /></Guard>} />
              <Route path="/systemstatus" element={<Guard menuKey="system"><SystemStatus /></Guard>} />
              <Route path="/einstellungen" element={<Guard menuKey="settings"><Settings /></Guard>} />
              {/* Routable V3 action pages on list-level screens. */}
              <Route path="/konto/aktion/:aktion" element={<Account />} />
              <Route path="/einstellungen/aktion/:aktion" element={<Guard menuKey="settings"><Settings /></Guard>} />
              <Route path="/systemstatus/aktion/:aktion" element={<Guard menuKey="system"><SystemStatus /></Guard>} />
              <Route path="/audit-log/aktion/:aktion" element={<Guard menuKey="audit"><AuditLogs /></Guard>} />
              <Route path="/pilot-auswertung/aktion/:aktion" element={<Guard menuKey="pilot"><Pilot /></Guard>} />
              <Route path="/team-rechte/aktion/:aktion" element={<Guard menuKey="team"><TeamRights /></Guard>} />
              <Route path="/aufgaben/aktion/:aktion" element={<Guard menuKey="tasks"><MyTasks /></Guard>} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
